/**
 * The crash-recovery record for the one unsaved new plan — what is written to the browser, and what
 * today's build accepts back from it.
 *
 * A plan the create modal seeds exists nowhere but this tab until its first Zapisz (`POST
 * /plan-template` refuses a plan without a real exercise, so there is no earlier moment to store
 * it). A reload used to destroy it. `lib/unsavedPlanRecord.ts` now mirrors that document into ONE
 * `localStorage` key per device; this module decides what the bytes in that key MEAN. It holds no
 * storage and no clock: the raw string, the signed-in user and `now` arrive as parameters, which is
 * what lets every rejection below be tested without a browser.
 *
 * ── The record outlives the build that wrote it ──────────────────────────────────────────────
 * A record is kept for seven days, and a deploy lands in between far more often than not. So the
 * reader never assumes today's shape: it rebuilds the tree field by field, and anything it cannot
 * read as today's document — a truncated write, a tree from a build that predates a field the
 * builder dereferences, a tree the builder would crash on — is DISCARDED rather than handed to a
 * builder that would throw on open, every open, until the record expired.
 *
 * Two kinds of evolution are handled differently, on purpose:
 *
 * · **The ENVELOPE is versioned** (`schema`). A change to what the record carries around the tree —
 *   a field renamed, a unit changed — bumps `UNSAVED_PLAN_RECORD_SCHEMA` and adds one step to
 *   `MIGRATIONS`, which walks an older envelope forward before it is read. A field ADDED to the
 *   envelope with an obvious absent meaning needs no bump: `documentKey` and `saveStartedAt` joined
 *   v1 that way, because a v1 reader that finds neither reads the record exactly as before.
 * · **The TREE is read, not versioned.** `PlanDraft` grows a field every few weeks and nobody should
 *   have to remember to bump a storage schema for it. A field whose absence has an obvious meaning —
 *   an absent nullable is `null`, an absent list of cycles or columns is empty — reads as that
 *   default. A field without one fails the read and the record goes. The rebuild is an object LITERAL
 *   typed as the draft, so adding a required field to `planDraft.types.ts` stops the build here until
 *   somebody decides which of the two it is. Unknown extra keys are dropped by the same rebuild.
 *
 * ── A NEWER build's record is never this build's to delete ───────────────────────────────────
 * A schema above this build's is what a rolled-back deploy, or a tab left open on last week's
 * bundle, finds after a newer tab wrote. It cannot be read here, and it is not broken either: it is
 * `newer`, ignored like another trainer's, and only the TTL removes it — and only when its `savedAt`
 * can be read at all.
 *
 * ── Why a thrown sentinel inside, and nothing thrown outside ─────────────────────────────────
 * Every field reader throws `UNREADABLE` the moment a value is not what the draft needs, and
 * `judgeUnsavedPlanRecord` catches EVERYTHING at one boundary. The alternative — every reader
 * returning a failure value its caller has to check — is five hundred lines of plumbing for the same
 * answer. The catch is deliberately total: a getter, a prototype oddity or a bug in this file must
 * still end in "no record", because the one thing this module may never do is take the builder down.
 */

import { ALL_MEASUREMENT_FIELD_KEYS, type MeasurementFieldKey } from '~/shared/measurement';

import { AUTHORED_ORIGIN } from './draftFactory';
import {
  COLLECTION_CAPS,
  DAY_OF_WEEK_RANGE,
  MAX_ABSOLUTE_CYCLE,
  TEXT_LIMITS,
} from './planCaps';
import type {
  DayDraft,
  ExerciseDraft,
  PhaseDraft,
  PlanDraft,
  PlanLiveContext,
  PlanOrigin,
  PlanSchedulingMode,
  PhaseType,
  ProgressionOverride,
  SetDraft,
} from './planDraft.types';
import type { ProgressionRule, ProgressionRules } from './progressionRules';

/** What every field reader throws, and the one boundary in `judgeUnsavedPlanRecord` catches. */
const UNREADABLE = new Error('unsaved plan record is unreadable');

const MEASUREMENT_FIELD_KEYS: ReadonlySet<string> = new Set<string>(ALL_MEASUREMENT_FIELD_KEYS);

/** The envelope version this build writes. Bump it only with a step in `MIGRATIONS`. */
export const UNSAVED_PLAN_RECORD_SCHEMA = 2;

/**
 * How long an unsaved plan is offered back. The phone's `CACHE_TTL_MS`: a week-old half-built plan
 * is stale enough that offering it is noise, and the record would otherwise live for ever on a
 * device nobody signs into again.
 */
export const UNSAVED_PLAN_RECORD_TTL_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * The client a plan is being built FOR. Nothing on `main` writes one yet: the copy-and-assign flow
 * (`feat/plan-copy-assign-flow`) seeds a draft with a recipient, and its record has to reopen with
 * that client still attached. It is part of the envelope now so that flow lands as a writer, not as
 * a schema change — and so a record written without one reads as `null` rather than failing.
 */
export interface UnsavedPlanRecipient {
  readonly clientId: number;
  readonly clientName: string;
}

export interface UnsavedPlanRecord {
  /** Epoch milliseconds of the write. What the TTL and the "ostatnia zmiana" line read. */
  readonly savedAt: number;
  /** The signed-in user's id (`SessionUser.id`) at the time of the write. */
  readonly userId: string;
  /** Always an unsaved plan — `id === null`. A tree with an id is refused; see `readDraft`. */
  readonly draft: PlanDraft;
  readonly recipient: UnsavedPlanRecipient | null;
  /**
   * Which unsaved DOCUMENT this is, minted once per seed. Two tabs building two plans share one key
   * on the device; this is what lets a tab that saves or starts afresh remove only its own plan's
   * copy. A v1 record written before the field falls back to its first day's slot token — stable
   * for that record, and never equal to a key a tab mints — and `null` when it has no day at all.
   */
  readonly documentKey: string | null;
  /**
   * When a first save of this plan was sent and never answered — the tab closed with the POST in
   * flight, or the network gave no verdict. The server may hold the plan already, so the prompt that
   * offers it back says so. `null` for every record written without a save outstanding.
   */
  readonly saveStartedAt: number | null;
}

/**
 * What the stored bytes amount to for this user, now.
 *
 * `foreign` and `newer` are their own verdicts because they are the two bad answers that must NOT
 * delete the record: another trainer's unsaved work on a shared computer, and a newer build's copy
 * this build cannot read. Both are ignored here and left to their owner, or to the TTL.
 */
export type UnsavedPlanRecordVerdict =
  | { readonly verdict: 'absent' }
  | { readonly verdict: 'usable'; readonly record: UnsavedPlanRecord }
  | { readonly verdict: 'foreign' }
  | { readonly verdict: 'newer' }
  | { readonly verdict: 'discard' };

/** The envelope as the NEXT schema's reader expects it, from the one before. */
type EnvelopeMigration = (envelope: Readonly<Record<string, unknown>>) => Record<string, unknown>;

/**
 * One step per schema, keyed by the schema it upgrades FROM. Empty while v1 is the only version
 * there has been. A v2 adds `1: (v1) => ({ ...v1, schema: 2, … })` and nothing else changes: the
 * reader walks every older record forward one step at a time, and a record whose step is missing is
 * discarded rather than guessed at.
 */
const MIGRATIONS: Readonly<Partial<Record<number, EnvelopeMigration>>> = {
  // v2 (30 September 2026) added the `aiDraft` origin. A v1 record cannot carry one, so it reads
  // unchanged; the bump exists for the builds BEFORE it, which judge a v2 record `newer` and leave
  // it alone instead of discarding an AI draft over an origin they cannot read.
  1: (v1) => ({ ...v1, schema: 2 }),
};

/** The string written to storage. The reader's exact inverse for the current schema. */
export function serialiseUnsavedPlanRecord(record: UnsavedPlanRecord): string {
  return JSON.stringify({
    // Schema 2 only where it is needed, a Buddy draft (the `aiDraft` origin). Every other record
    // stays schema 1, which the builds before 2 read and offer back as they always did.
    schema: record.draft.origin.kind === 'aiDraft' ? UNSAVED_PLAN_RECORD_SCHEMA : 1,
    savedAt: record.savedAt,
    userId: record.userId,
    draft: record.draft,
    recipient: record.recipient,
    documentKey: record.documentKey,
    saveStartedAt: record.saveStartedAt,
  });
}

/**
 * Judge the stored bytes. **Never throws, for any input** — see the header.
 *
 * The order is the order of consequence. Expiry first, and on the raw `savedAt`, because it is the
 * one rule that applies to every record this build can date — a newer build's included. Then the
 * schema, because a newer one is left alone unread. Then ownership BEFORE the tree, because another
 * trainer's record is ignored whatever it contains.
 */
export function judgeUnsavedPlanRecord(
  raw: string | null,
  userId: string,
  now: number,
): UnsavedPlanRecordVerdict {
  if (raw === null) return { verdict: 'absent' };
  try {
    const stored = asObject(JSON.parse(raw));
    const savedAt =
      typeof stored.savedAt === 'number' && Number.isFinite(stored.savedAt) ? stored.savedAt : null;
    // Absolute, so a clock set back does not keep a record alive for a week past its write.
    if (savedAt !== null && Math.abs(now - savedAt) > UNSAVED_PLAN_RECORD_TTL_MS) {
      return { verdict: 'discard' };
    }
    const schema = integer(stored.schema);
    if (schema > UNSAVED_PLAN_RECORD_SCHEMA) return { verdict: 'newer' };
    const envelope = migrateEnvelope(stored, schema);
    if (savedAt === null) throw UNREADABLE;
    if (string(envelope.userId) !== userId) return { verdict: 'foreign' };
    const draft = readDraft(envelope.draft);
    return {
      verdict: 'usable',
      record: {
        savedAt,
        userId,
        draft,
        recipient: readRecipient(envelope.recipient),
        documentKey:
          envelope.documentKey === undefined || envelope.documentKey === null
            ? fallbackDocumentKey(draft)
            : string(envelope.documentKey),
        saveStartedAt: nullable(envelope.saveStartedAt, finiteNumber),
      },
    };
  } catch {
    return { verdict: 'discard' };
  }
}

/** A v1 record written before `documentKey` existed: its first day's token names it. */
function fallbackDocumentKey(draft: PlanDraft): string | null {
  return draft.phases[0]?.days[0]?.slotToken ?? null;
}

// ─────────────────────────────────────────────────────────────────────────────────────────────
// The envelope.
// ─────────────────────────────────────────────────────────────────────────────────────────────

function migrateEnvelope(
  envelope: Readonly<Record<string, unknown>>,
  from: number,
): Readonly<Record<string, unknown>> {
  if (from < 1) throw UNREADABLE;
  let current = envelope;
  for (let schema = from; schema < UNSAVED_PLAN_RECORD_SCHEMA; schema += 1) {
    const step = MIGRATIONS[schema];
    if (step === undefined) throw UNREADABLE;
    current = step(current);
  }
  return current;
}

function readRecipient(value: unknown): UnsavedPlanRecipient | null {
  if (value === undefined || value === null) return null;
  const o = asObject(value);
  return { clientId: finiteNumber(o.clientId), clientName: string(o.clientName) };
}

// ─────────────────────────────────────────────────────────────────────────────────────────────
// The tree. One reader per node, each an object literal typed as the node — see the header for why
// the literal is the point. Counts, cycles and identities are held to the SAME ceilings the entity
// holds an edit to (`planCaps.ts`), because a value past them is one the builder was never written
// to survive: a phase of 1e10 cycles is a `RangeError` in `contiguousCycles`, two days sharing a
// slot token is a card the week cannot address.
// ─────────────────────────────────────────────────────────────────────────────────────────────

const SCHEDULING_MODES: readonly PlanSchedulingMode[] = ['date', 'selfpaced'];
const PHASE_TYPES: readonly PhaseType[] = ['build', 'deload'];
const DELTA_UNITS: readonly ProgressionRule['deltaUnit'][] = ['Absolute', 'Percent'];

function readDraft(value: unknown): PlanDraft {
  const o = asObject(value);
  // Only an UNSAVED plan is ever written. A tree carrying an id is a stored plan's cache, and
  // reopening it as a new plan would POST a second copy of it on the first Zapisz.
  if (o.id !== null && o.id !== undefined) throw UNREADABLE;
  const identities = new Set<string>();
  const draft: PlanDraft = {
    id: null,
    name: string(o.name),
    description: nullable(o.description, string),
    category: nullable(o.category, string),
    coverId: nullable(o.coverId, finiteNumber),
    coverImageUrl: nullable(o.coverImageUrl, string),
    mode: oneOf(o.mode, SCHEDULING_MODES),
    isLibraryEntry: boolean(o.isLibraryEntry),
    origin: readOrigin(o.origin),
    phases: bounded(o.phases, COLLECTION_CAPS.phasesPerPlan, (phase) => readPhase(phase, identities)),
    live: readLive(o.live),
  };
  return draft;
}

/**
 * Where the plan came from — what its assignment declares (`PlanOrigin`).
 *
 * ABSENT reads as `authored`: every record written before the field existed (23 September 2026)
 * was a plan started from nothing, because the create modal could not copy a template until then.
 * An UNKNOWN kind is refused, not read as `authored`. It can only come from a newer build that
 * knows a lineage this one does not, and `authored` is not a safe guess about it: assigning a copy
 * as authored leaves its template reporting no clients — the lie mobile's reader was changed to
 * refuse. A malformed `copyOf` (no positive integer id) is refused for the same reason.
 */
function readOrigin(value: unknown): PlanOrigin {
  if (value === undefined || value === null) return AUTHORED_ORIGIN;
  const o = asObject(value);
  if (o.kind === 'authored') return AUTHORED_ORIGIN;
  if (o.kind === 'copyOf') {
    return { kind: 'copyOf', templateId: within(o.templateId, { min: 1, max: Number.MAX_SAFE_INTEGER }) };
  }
  if (o.kind === 'aiDraft') {
    const conversationId = within(o.conversationId, { min: 1, max: Number.MAX_SAFE_INTEGER });
    // Optional since 3 October 2026, and dropped rather than refused when it is not a key: the
    // draft is still the trainer's work, only the key's clean-up is lost.
    const draftKey = typeof o.draftKey === 'string' && /^[A-Za-z0-9-]{8,64}$/.test(o.draftKey) ? o.draftKey : null;
    return draftKey === null ? { kind: 'aiDraft', conversationId } : { kind: 'aiDraft', conversationId, draftKey };
  }
  throw UNREADABLE;
}

/**
 * An unsaved plan has never been stored, assigned or trained, so every server-owned fact about it
 * is absent. A context carrying one — a fork flag, a client, a consumed cycle, a version to save
 * against — is a stored plan's, and opening it as new would draw locks and a client that are not
 * there. The two nullable collections keep whichever empty form they were written with.
 */
function readLive(value: unknown): PlanLiveContext {
  const o = asObject(value);
  if (o.isForkTemplate !== false) throw UNREADABLE;
  absent(o.clientName);
  absent(o.firstOpenCycle);
  absent(o.lastConsumedCycle);
  absent(o.baseVersion);
  absent(o.executionMode);
  absent(o.serverStructure);
  return {
    isForkTemplate: false,
    clientName: null,
    firstOpenCycle: null,
    lastConsumedCycle: null,
    totalCycles: nullable(o.totalCycles, cycle),
    baseVersion: null,
    executionMode: null,
    serverStructure: null,
    cyclesClosedToNewDays: nullable(o.cyclesClosedToNewDays, (closed) =>
      bounded(closed, { min: 0, max: 0 }, cycle),
    ),
  };
}

function readPhase(value: unknown, identities: Set<string>): PhaseDraft {
  const o = asObject(value);
  return {
    serverId: nullable(o.serverId, finiteNumber),
    name: string(o.name),
    type: oneOf(o.type, PHASE_TYPES),
    cycles: within(o.cycles, COLLECTION_CAPS.cyclesPerPhase),
    startCycle: cycle(o.startCycle),
    days: bounded(o.days, { min: 0, max: COLLECTION_CAPS.daysPerPhase.max }, (day) =>
      readDay(day, identities),
    ),
  };
}

function readDay(value: unknown, identities: Set<string>): DayDraft {
  const o = asObject(value);
  return {
    slotToken: identity(o.slotToken, identities),
    serverId: nullable(o.serverId, finiteNumber),
    serverOrder: nullable(o.serverOrder, finiteNumber),
    name: nullable(o.name, string),
    sessionName: nullable(o.sessionName, string),
    sessionNotes: nullable(o.sessionNotes, string),
    dayOfWeek: nullable(o.dayOfWeek, (day) => within(day, DAY_OF_WEEK_RANGE)),
    restDaysAfter: nullable(o.restDaysAfter, finiteNumber),
    activationCycle: nullable(o.activationCycle, cycle),
    retirementCycle: nullable(o.retirementCycle, cycle),
    excludedCycles: cycles(o.excludedCycles),
    serverCycles: nullable(o.serverCycles, cycles),
    liveClosedCycles: nullable(o.liveClosedCycles, cycles),
    durationMinutes: nullable(o.durationMinutes, finiteNumber),
    restSeconds: nullable(o.restSeconds, finiteNumber),
    exercises: bounded(o.exercises, { min: 0, max: COLLECTION_CAPS.exercisesPerSession.max }, (e) =>
      readExercise(e, identities),
    ),
  };
}

function readExercise(value: unknown, identities: Set<string>): ExerciseDraft {
  const o = asObject(value);
  return {
    slotToken: identity(o.slotToken, identities),
    exerciseId: finiteNumber(o.exerciseId),
    name: string(o.name),
    activationCycle: nullable(o.activationCycle, cycle),
    typeKey: nullable(o.typeKey, string),
    role: string(o.role),
    // No default and never empty: the columns the set table draws are derived from these, and an
    // exercise that measures nothing is not a shape any build ever wrote.
    measurementFields: bounded(
      o.measurementFields,
      { min: 1, max: ALL_MEASUREMENT_FIELD_KEYS.length },
      measurementField,
    ),
    progressionRules: readProgressionRules(o.progressionRules),
    unreadableProgressionRules:
      o.unreadableProgressionRules === undefined ? {} : asObject(o.unreadableProgressionRules),
    realMaxMultiplier: nullable(o.realMaxMultiplier, finiteNumber),
    realMaxSource: nullable(o.realMaxSource, string),
    retirementCycle: nullable(o.retirementCycle, cycle),
    excludedCycles: cycles(o.excludedCycles),
    serverCycles: nullable(o.serverCycles, cycles),
    progressionOverrides:
      o.progressionOverrides === undefined
        ? []
        : bounded(o.progressionOverrides, COLLECTION_CAPS.overridesPerExercise, readOverride),
    prescriptionMode: string(o.prescriptionMode),
    group: nullable(o.group, string),
    notes: nullable(o.notes, string),
    revealedColumns:
      o.revealedColumns === undefined
        ? []
        : bounded(o.revealedColumns, { min: 0, max: ALL_MEASUREMENT_FIELD_KEYS.length }, measurementField),
    showsRestColumn: o.showsRestColumn === undefined ? false : boolean(o.showsRestColumn),
    sets: bounded(o.sets, { min: 0, max: COLLECTION_CAPS.setsPerExercise.max }, (set) =>
      readSet(set, identities),
    ),
    executions: readExecutions(o.executions),
  };
}

/**
 * Keyed by the column each rule writes. A rule's optional `wireDocument` — the stored JSON the
 * backend echoes back — is kept whole: this app deliberately never interprets its inside, so the
 * check stops at the fields the builder reads.
 */
function readProgressionRules(value: unknown): ProgressionRules {
  if (value === undefined) return {};
  const rules: Partial<Record<MeasurementFieldKey, ProgressionRule>> = {};
  for (const [field, rule] of Object.entries(asObject(value))) {
    rules[measurementField(field)] = readRule(rule);
  }
  return rules;
}

function readRule(value: unknown): ProgressionRule {
  const o = asObject(value);
  const deltaUnit = oneOf(o.deltaUnit, DELTA_UNITS);
  const wire = o.wireDocument === undefined ? {} : { wireDocument: asObject(o.wireDocument) };
  if (o.type === 'PreviousExecution') {
    return {
      type: 'PreviousExecution',
      banded: nullable(o.banded, measurementField),
      delta: finiteNumber(o.delta),
      deltaUnit,
      ...wire,
    };
  }
  if (o.type === 'Static') {
    return { type: 'Static', delta: nullable(o.delta, finiteNumber), deltaUnit, ...wire };
  }
  throw UNREADABLE;
}

/**
 * The per-cycle override is a SPARSE overlay: a column absent from it reverts to the base, a column
 * present as `null` pins an empty cell. So an absent key stays absent here — defaulting it to `null`
 * would silently turn "not overridden" into "overridden to nothing".
 */
function readOverride(value: unknown): ProgressionOverride {
  const o = asObject(value);
  if (o.isManuallyOverridden !== true) throw UNREADABLE;
  return {
    setIndex: within(o.setIndex, COLLECTION_CAPS.setsPerExercise),
    cycleIndex: cycle(o.cycleIndex),
    prescribedWeight: nullable(o.prescribedWeight, finiteNumber),
    prescribedPercent: nullable(o.prescribedPercent, finiteNumber),
    prescribedRpe: nullable(o.prescribedRpe, finiteNumber),
    prescribedRir: nullable(o.prescribedRir, finiteNumber),
    ...sparse(o, 'prescribedReps', finiteNumber),
    ...sparse(o, 'prescribedRestTime', finiteNumber),
    ...sparse(o, 'prescribedDurationSeconds', finiteNumber),
    ...sparse(o, 'prescribedDistanceMeters', finiteNumber),
    ...sparse(o, 'prescribedRounds', finiteNumber),
    ...sparse(o, 'prescribedSpeed', finiteNumber),
    ...sparse(o, 'prescribedTempo', string),
    isManuallyOverridden: true,
  };
}

function readSet(value: unknown, identities: Set<string>): SetDraft {
  const o = asObject(value);
  return {
    // The row key the set table renders by: persisted with the record precisely so a restored
    // tree keeps it, and unique for the same reason a slot token is.
    localId: identity(o.localId, identities),
    setIndex: within(o.setIndex, COLLECTION_CAPS.setsPerExercise),
    setType: nullable(o.setType, string),
    prescribedWeight: nullable(o.prescribedWeight, finiteNumber),
    prescribedReps: nullable(o.prescribedReps, finiteNumber),
    prescribedRpe: nullable(o.prescribedRpe, finiteNumber),
    prescribedRir: nullable(o.prescribedRir, finiteNumber),
    prescribedRestTime: nullable(o.prescribedRestTime, finiteNumber),
    prescribedDurationSeconds: nullable(o.prescribedDurationSeconds, finiteNumber),
    prescribedDistanceMeters: nullable(o.prescribedDistanceMeters, finiteNumber),
    prescribedRounds: nullable(o.prescribedRounds, finiteNumber),
    prescribedPercent: nullable(o.prescribedPercent, finiteNumber),
    prescribedTempo: nullable(o.prescribedTempo, string),
    prescribedSpeed: nullable(o.prescribedSpeed, finiteNumber),
    note: nullable(o.note, string),
    prescribedCeiling: nullable(o.prescribedCeiling, finiteNumber),
  };
}

/**
 * An unsaved plan has never been delivered to anybody, so it has no executions — `null` (the
 * surface did not say) or an empty list. A non-empty history is server-owned identity that no
 * unsaved plan can honestly carry, and rather than validate a shape it should not have, the record
 * is refused.
 */
function readExecutions(value: unknown): ExerciseDraft['executions'] {
  if (value === undefined || value === null) return null;
  if (!Array.isArray(value) || value.length > 0) throw UNREADABLE;
  return [];
}

// ─────────────────────────────────────────────────────────────────────────────────────────────
// Leaf readers. Each returns the value as the draft types it, or throws `UNREADABLE`.
// ─────────────────────────────────────────────────────────────────────────────────────────────

interface Range {
  readonly min: number;
  readonly max: number;
}

function asObject(value: unknown): Readonly<Record<string, unknown>> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) throw UNREADABLE;
  return value as Readonly<Record<string, unknown>>;
}

function string(value: unknown): string {
  if (typeof value !== 'string') throw UNREADABLE;
  return value;
}

function boolean(value: unknown): boolean {
  if (typeof value !== 'boolean') throw UNREADABLE;
  return value;
}

function finiteNumber(value: unknown): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) throw UNREADABLE;
  return value;
}

function integer(value: unknown): number {
  const n = finiteNumber(value);
  if (!Number.isInteger(n)) throw UNREADABLE;
  return n;
}

/** An integer inside a stated range — a count, a position, a weekday. */
function within(value: unknown, range: Range): number {
  const n = integer(value);
  if (n < range.min || n > range.max) throw UNREADABLE;
  return n;
}

/** An absolute cycle, held to the plan-wide ceiling every cycle projection walks up to. */
function cycle(value: unknown): number {
  return within(value, { min: 1, max: MAX_ABSOLUTE_CYCLE });
}

/** A field that must be absent on an unsaved plan. */
function absent(value: unknown): void {
  if (value !== undefined && value !== null) throw UNREADABLE;
}

/** A slot token or a set's local id: a bounded string, unique across the whole tree. */
function identity(value: unknown, seen: Set<string>): string {
  const token = string(value);
  if (token.length < TEXT_LIMITS.slotToken.min || token.length > TEXT_LIMITS.slotToken.max) {
    throw UNREADABLE;
  }
  if (seen.has(token)) throw UNREADABLE;
  seen.add(token);
  return token;
}

function oneOf<T extends string>(value: unknown, allowed: readonly T[]): T {
  if (typeof value !== 'string' || !(allowed as readonly string[]).includes(value)) {
    throw UNREADABLE;
  }
  return value as T;
}

function measurementField(value: unknown): MeasurementFieldKey {
  if (typeof value !== 'string' || !MEASUREMENT_FIELD_KEYS.has(value)) throw UNREADABLE;
  return value as MeasurementFieldKey;
}

/** An absent nullable field reads as `null` — the one default that is obvious for every such field. */
function nullable<T>(value: unknown, read: (value: unknown) => T): T | null {
  return value === undefined || value === null ? null : read(value);
}

/** A list the builder walks, its length held to the entity's cap. Absent is a broken tree. */
function bounded<T>(value: unknown, range: Range, read: (value: unknown) => T): T[] {
  if (!Array.isArray(value) || value.length < range.min || value.length > range.max) {
    throw UNREADABLE;
  }
  return value.map((item: unknown) => read(item));
}

/** A list of cycles whose absence means "none" — excluded cycles, the server's resolution. */
function cycles(value: unknown): number[] {
  return value === undefined ? [] : bounded(value, { min: 0, max: MAX_ABSOLUTE_CYCLE }, cycle);
}

/** A sparse key: carried when present, absent when absent — never defaulted. See `readOverride`. */
function sparse<K extends string, T>(
  o: Readonly<Record<string, unknown>>,
  key: K,
  read: (value: unknown) => T,
): Partial<Record<K, T | null>> {
  if (o[key] === undefined) return {};
  const present: Partial<Record<K, T | null>> = {};
  present[key] = nullable(o[key], read);
  return present;
}

/**
 * May this tab write its plan into the one device key, given what the key holds right now?
 *
 * The key is SHARED — by every tab of this browser, by every trainer who signs in here, and by
 * every build that has been deployed since. A write is a blind overwrite unless it asks first, and
 * each of those neighbours has lost work to one: a second tab's seed replaced the first tab's plan,
 * a new trainer's seed replaced an expired colleague's, an old tab replaced a newer build's copy.
 *
 * So the slot is free only when it is empty, dead (expired or unreadable), or already THIS
 * document's copy for THIS user. Anything else — another live document, another trainer, a newer
 * schema — is left alone, and the tab reports that the device does not hold its plan.
 */
export function mayWriteUnsavedPlanRecord(
  raw: string | null,
  userId: string,
  documentKey: string,
  now: number,
): boolean {
  const judged = judgeUnsavedPlanRecord(raw, userId, now);
  switch (judged.verdict) {
    case 'absent':
    case 'discard':
      return true;
    case 'usable':
      return judged.record.documentKey === documentKey;
    case 'foreign':
    case 'newer':
      return false;
  }
}

/** Does the key hold THIS document's copy for this user? What `held` means, read off the bytes. */
export function recordHoldsDocument(
  raw: string | null,
  userId: string,
  documentKey: string,
  now: number,
): boolean {
  const judged = judgeUnsavedPlanRecord(raw, userId, now);
  return judged.verdict === 'usable' && judged.record.documentKey === documentKey;
}
